import { useMemo, useState, type ReactNode } from 'react';
import { ArrowDown, ArrowUp, ChevronsUpDown } from 'lucide-react';
import { cn } from '../../lib/cn.js';
import { SkeletonTable } from '../ui/primitives.js';

/**
 * The catalog's table.
 *
 * Hand-rolled rather than pulling a grid library, for one reason: a table
 * library gives you virtualisation and pivoting we do not need, and does not
 * give you the thing we do need - a genuinely different layout on a phone.
 * Below `lg` each row becomes a card, because a 12-column table in a 390px
 * viewport is a horizontal-scroll puzzle, not a table.
 */

export interface Column<T> {
  key: string;
  header: string;
  /**
   * Cell content. Keep it presentational - sorting uses `sortValue`.
   * `index` is the row's place in the list as displayed - after filtering and
   * sorting - counting from 0.
   */
  render: (row: T, index: number) => ReactNode;
  /** Sortable when provided. */
  sortValue?: (row: T) => string | number | null | undefined;
  className?: string;
  headerClassName?: string;
  /** Hide below `lg`; the card layout shows it in the meta line instead. */
  hideOnMobile?: boolean;
  /** Promote to the card's title line on mobile. */
  primary?: boolean;
}

export interface DataTableProps<T> {
  rows: T[] | undefined;
  columns: Array<Column<T>>;
  rowKey: (row: T) => string;
  onRowClick?: (row: T) => void;
  /** Right-aligned per-row actions; rendered outside the click target. */
  rowActions?: (row: T) => ReactNode;
  empty?: ReactNode;
  loadingRows?: number;
  initialSort?: { key: string; direction: 'asc' | 'desc' };
  /** Rendered as a leading visual in the mobile card (e.g. a photo). */
  mobileLeading?: (row: T) => ReactNode;
  /**
   * Row selection, for acting on many rows at once. Omit and no checkboxes
   * appear at all - a column of them on a screen with no bulk action is a
   * control that does nothing.
   */
  selection?: {
    selected: ReadonlySet<string>;
    onChange: (next: Set<string>) => void;
    /** Rows that can be picked; the others get no checkbox. Every row when omitted. */
    canSelect?: (row: T) => boolean;
    /** What a row's checkbox is called, when "Select row" says too little. */
    label?: (row: T) => string;
  };
}

export function DataTable<T>({
  rows,
  columns,
  rowKey,
  onRowClick,
  rowActions,
  empty,
  loadingRows = 8,
  initialSort,
  mobileLeading,
  selection,
}: DataTableProps<T>) {
  const [sort, setSort] = useState<{ key: string; direction: 'asc' | 'desc' } | null>(
    initialSort ?? null,
  );

  const sorted = useMemo(() => {
    if (!rows) return rows;
    if (!sort) return rows;
    const column = columns.find((c) => c.key === sort.key);
    if (!column?.sortValue) return rows;

    const factor = sort.direction === 'asc' ? 1 : -1;
    return [...rows].sort((a, b) => {
      const av = column.sortValue!(a);
      const bv = column.sortValue!(b);
      // Empty cells sort last in both directions - a blank is not "smallest",
      // it is missing, and burying it under the data is rarely what you want.
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * factor;
      return String(av).localeCompare(String(bv), undefined, { numeric: true }) * factor;
    });
  }, [rows, sort, columns]);

  function toggleSort(key: string) {
    setSort((prev) =>
      prev?.key === key
        ? prev.direction === 'asc'
          ? { key, direction: 'desc' }
          : null // third click clears - back to the natural order
        : { key, direction: 'asc' },
    );
  }

  /**
   * Select-all acts on the rows currently *on screen*, not the whole table.
   * After filtering to one project, "select all" meaning the other sixty is a
   * bulk edit nobody asked for.
   */
  const selectable = (row: T) => selection?.canSelect?.(row) ?? true;
  const visibleIds = sorted?.filter(selectable).map(rowKey) ?? [];
  const allSelected = visibleIds.length > 0 && visibleIds.every((id) => selection?.selected.has(id));
  const someSelected = visibleIds.some((id) => selection?.selected.has(id)) && !allSelected;

  function toggleAll() {
    if (!selection) return;
    const next = new Set(selection.selected);
    if (allSelected) visibleIds.forEach((id) => next.delete(id));
    else visibleIds.forEach((id) => next.add(id));
    selection.onChange(next);
  }

  function toggleOne(id: string) {
    if (!selection) return;
    const next = new Set(selection.selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    selection.onChange(next);
  }

  if (!sorted) {
    return (
      <div className="overflow-hidden rounded-xl border border-border bg-surface">
        <SkeletonTable rows={loadingRows} cols={Math.min(columns.length, 6)} />
      </div>
    );
  }

  if (sorted.length === 0) {
    return <div className="rounded-xl border border-border bg-surface">{empty}</div>;
  }

  return (
    <>
      {/* --- desktop --- */}
      <div className="hidden overflow-hidden rounded-xl border border-border bg-surface lg:block">
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr className="border-b border-border bg-surface-sunken/60">
                {selection ? (
                  <th scope="col" className="w-10 px-3 py-2">
                    <input
                      type="checkbox"
                      className="size-4 align-middle"
                      checked={allSelected}
                      disabled={visibleIds.length === 0}
                      ref={(el) => {
                        // The third state: some but not all. Without it, "select
                        // all" looks unticked while six rows are selected.
                        if (el) el.indeterminate = someSelected;
                      }}
                      onChange={toggleAll}
                      aria-label={allSelected ? 'Clear selection' : 'Select all rows shown'}
                    />
                  </th>
                ) : null}
                {columns.map((col) => (
                  <th
                    key={col.key}
                    scope="col"
                    aria-sort={
                      sort?.key === col.key
                        ? sort.direction === 'asc'
                          ? 'ascending'
                          : 'descending'
                        : undefined
                    }
                    className={cn(
                      'px-3 py-2 text-left text-xs font-semibold text-muted-foreground',
                      col.headerClassName,
                    )}
                  >
                    {col.sortValue ? (
                      <button
                        type="button"
                        onClick={() => toggleSort(col.key)}
                        className="inline-flex items-center gap-1 hover:text-foreground"
                      >
                        {col.header}
                        <SortIcon active={sort?.key === col.key} direction={sort?.direction} />
                      </button>
                    ) : (
                      col.header
                    )}
                  </th>
                ))}
                {rowActions ? <th className="w-10 px-3 py-2" /> : null}
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {sorted.map((row, index) => (
                <tr
                  key={rowKey(row)}
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                  className={cn(
                    // `group` so a sticky cell can paint the row's hover colour
                    // itself - a pinned column sits above the row background and
                    // would otherwise stay stubbornly un-highlighted.
                    'group transition-colors',
                    onRowClick && 'cursor-pointer hover:bg-surface-sunken/70',
                    selection?.selected.has(rowKey(row)) && 'bg-primary-subtle/40',
                  )}
                >
                  {selection ? (
                    <td className="px-3 py-2 align-middle" onClick={(e) => e.stopPropagation()}>
                      {selectable(row) ? (
                        <input
                          type="checkbox"
                          className="size-4 align-middle"
                          checked={selection.selected.has(rowKey(row))}
                          onChange={() => toggleOne(rowKey(row))}
                          aria-label={selection.label?.(row) ?? 'Select row'}
                        />
                      ) : null}
                    </td>
                  ) : null}
                  {columns.map((col) => (
                    <td key={col.key} className={cn('px-3 py-2 align-middle', col.className)}>
                      {col.render(row, index)}
                    </td>
                  ))}
                  {rowActions ? (
                    <td
                      className="px-3 py-2 text-right"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {rowActions(row)}
                    </td>
                  ) : null}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* --- mobile --- */}
      <ul className="flex flex-col gap-2 lg:hidden">
        {sorted.map((row, index) => {
          const primary = columns.filter((c) => c.primary);
          const meta = columns.filter((c) => !c.primary && !c.hideOnMobile);
          return (
            <li key={rowKey(row)}>
              <div
                role={onRowClick ? 'button' : undefined}
                tabIndex={onRowClick ? 0 : undefined}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
                onKeyDown={
                  onRowClick
                    ? (e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          onRowClick(row);
                        }
                      }
                    : undefined
                }
                className={cn(
                  'flex items-start gap-3 rounded-xl border border-border bg-surface p-3',
                  onRowClick && 'active:bg-surface-sunken',
                  selection?.selected.has(rowKey(row)) && 'border-primary bg-primary-subtle/40',
                )}
              >
                {selection && selectable(row) ? (
                  <input
                    type="checkbox"
                    className="mt-1 size-4 shrink-0"
                    checked={selection.selected.has(rowKey(row))}
                    onClick={(e) => e.stopPropagation()}
                    onChange={() => toggleOne(rowKey(row))}
                    aria-label={selection.label?.(row) ?? 'Select row'}
                  />
                ) : selection ? (
                  // Keeps the cards lined up.
                  <span className="mt-1 size-4 shrink-0" aria-hidden />
                ) : null}
                {mobileLeading ? <div className="shrink-0">{mobileLeading(row)}</div> : null}
                <div className="min-w-0 flex-1">
                  {primary.map((col) => (
                    <div key={col.key} className="min-w-0">
                      {col.render(row, index)}
                    </div>
                  ))}
                  {meta.length ? (
                    <dl className="mt-1.5 flex flex-wrap gap-x-3 gap-y-1">
                      {meta.map((col) => (
                        <div key={col.key} className="flex items-baseline gap-1 text-xs">
                          <dt className="text-subtle-foreground">{col.header}</dt>
                          <dd className="text-muted-foreground">{col.render(row, index)}</dd>
                        </div>
                      ))}
                    </dl>
                  ) : null}
                </div>
                {rowActions ? (
                  <div className="shrink-0" onClick={(e) => e.stopPropagation()}>
                    {rowActions(row)}
                  </div>
                ) : null}
              </div>
            </li>
          );
        })}
      </ul>
    </>
  );
}

function SortIcon({ active, direction }: { active: boolean; direction?: 'asc' | 'desc' }) {
  if (!active) return <ChevronsUpDown className="size-3 opacity-40" aria-hidden />;
  return direction === 'asc' ? (
    <ArrowUp className="size-3" aria-hidden />
  ) : (
    <ArrowDown className="size-3" aria-hidden />
  );
}
